import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../supabase';
import axios from 'axios';
import { needs2FA } from '../../../utils/twoFactor';
import logo from '../../../assets/logo800.webp';
import packageJson from '../../../../package.json';
import '../../styles/AdminLogin.css';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [otpSent, setOtpSent] = useState(() => {
    return sessionStorage.getItem('admin_otp_flow') === 'true';
  });
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [isProcessing2FA, setIsProcessing2FA] = useState(false);
  const [showSelfRegister, setShowSelfRegister] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [registerSent, setRegisterSent] = useState(false);
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

  // Restore email if in OTP flow
  useEffect(() => {
    const storedEmail = sessionStorage.getItem('admin_otp_email');
    if (storedEmail && otpSent) {
      setEmail(storedEmail);
    }
  }, [otpSent]);

  // Countdown timer for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Check if admin is already logged in
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Don't redirect (or sign out!) while mid-2FA - checked against sessionStorage
      // directly rather than the closed-over otpSent/isProcessing2FA state, since
      // this callback's closure can still hold stale values from before the most
      // recent render (e.g. right as signInWithPassword's own SIGNED_IN event fires),
      // which was intermittently signing out a session the OTP step still needed.
      if (isProcessing2FA || otpSent || sessionStorage.getItem('admin_otp_flow') === 'true') {
        return;
      }

      const user = session?.user;
      if (user) {
        try {
          const { data, error } = await supabase.auth.getUser();
          if (error) throw error;
          if (data.user.app_metadata?.admin === true) {
            if (await needs2FA(user.email)) {
              // AdminAuthAction.jsx sets this right after a real password-setup
              // sign-in, so the person lands straight on the OTP screen here
              // instead of retyping an email+password they only just proved
              // they know. Anything else with a signed-in-but-unverified
              // session (a different tab where the OTP step was abandoned) is
              // genuinely stale - sign out so this always lands on a clean
              // login form instead of bouncing between here and ProtectedRoute.
              if (sessionStorage.getItem('admin_auto_continue') === 'true') {
                sessionStorage.removeItem('admin_auto_continue');
                try {
                  await axios.post(`${BACKEND_URL}/api/send-otp`, { email: user.email.toLowerCase() });
                  setEmail(user.email);
                  sessionStorage.setItem('admin_otp_flow', 'true');
                  sessionStorage.setItem('admin_otp_email', user.email);
                  setOtpSent(true);
                  setResendTimer(60);
                } catch (otpError) {
                  console.error('Auto-continue OTP send error:', otpError);
                  await supabase.auth.signOut();
                }
                return;
              }
              await supabase.auth.signOut();
              return;
            }
            navigate('/');
          }
        } catch (err) {
          console.error('Error checking admin status:', err);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, isProcessing2FA, otpSent, BACKEND_URL]);

  // Handle OTP resend
  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${BACKEND_URL}/api/send-otp`, {
        email: email.toLowerCase()
      });
      setResendTimer(60);
      setOtp('');
      setLoading(false);
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError('Kunne ikke sende ny kode. Prøv igjen.');
      setLoading(false);
    }
  };

  const handleSelfRegister = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterError('');
    try {
      await axios.post(`${BACKEND_URL}/api/admins/self-register`, {
        email: registerEmail.trim().toLowerCase(),
        username: registerName.trim(),
      });
      setRegisterSent(true);
    } catch (err) {
      setRegisterError(err.response?.data?.error || 'Kunne ikke registrere deg. Prøv igjen.');
    }
    setRegisterLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Step 1: If OTP not sent yet, verify credentials and check if 2FA is needed
    if (!otpSent) {
      if (!email.trim() || !password.trim()) {
        setError('Vennligst fyll inn både e-post og passord');
        setLoading(false);
        return;
      }
      
      try {
        // Set processing flag BEFORE signing in
        setIsProcessing2FA(true);

        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email: email.trim(), password,
        });
        if (signInError) {
          setIsProcessing2FA(false);
          // Supabase deliberately doesn't distinguish "no such user" from "wrong
          // password" (anti-enumeration) - one generic message covers both.
          if (signInError.code === 'too_many_requests') {
            setError('For mange mislykkede forsøk. Prøv igjen senere.');
          } else {
            setError('Feil e-post eller passord. Prøv igjen.');
          }
          setLoading(false);
          return;
        }
        const user = signInData.user;

        // Check if user has admin claim
        if (user.app_metadata?.admin !== true) {
          await supabase.auth.signOut();
          setIsProcessing2FA(false);
          setError('Tilgang nektet. Du har ikke admin-rettigheter.');
          setLoading(false);
          return;
        }

        // Check if 2FA is required this calendar month
        const require2FA = await needs2FA(email.trim());

        if (require2FA) {
          try {
            await axios.post(`${BACKEND_URL}/api/send-otp`, {
              email: email.toLowerCase()
            });

            // Store flow state in sessionStorage - stays signed in through this step
            // (ProtectedRoute independently blocks admin pages until 2FA is fresh, so
            // there's no need to sign out, and no need to keep the password around to
            // sign back in afterward).
            sessionStorage.setItem('admin_otp_flow', 'true');
            sessionStorage.setItem('admin_otp_email', email.trim());

            // Show OTP input field
            setOtpSent(true);
            setResendTimer(60);
            setError('');
            setLoading(false);
          } catch (otpError) {
            console.error('OTP send error:', otpError);
            setError('Kunne ikke sende verifiseringskode. Prøv igjen.');
            await supabase.auth.signOut();
            setIsProcessing2FA(false);
            setLoading(false);
          }
        } else {
          // 2FA not required, successful login
          setIsProcessing2FA(false);

          // Store admin session
          localStorage.setItem('adminSession', JSON.stringify({
            email: user.email,
            loginTime: Date.now()
          }));

          setIsProcessing2FA(false);
          setLoginSuccess(true);

          setTimeout(() => {
            navigate('/');
          }, 1500);
        }

      } catch (err) {
        console.error('❌ Login error:', err);
        setIsProcessing2FA(false);
        setError('Innlogging feilet. Prøv igjen.');
        
        setLoading(false);
      }
    }
    // Step 2: OTP sent, now verify OTP
    else {
      if (otp.length !== 6) {
        setError('Vennligst skriv inn den 6-sifrede koden');
        setLoading(false);
        return;
      }

      try {
        // Get stored email (the session stayed signed in through this whole step -
        // no password to recall, and none was ever kept in browser storage)
        const storedEmail = sessionStorage.getItem('admin_otp_email') || email.trim();

        // verify-otp is a public backend endpoint keyed by email+code alone - it
        // needs no Supabase session at all, so there's nothing to gate on here.
        // (A prior version checked getSession()/getUser() first and would
        // false-positive "session expired" on a real, working session.)
        const response = await axios.post(`${BACKEND_URL}/api/verify-otp`, {
          email: storedEmail.toLowerCase(),
          otp: otp
        });

        if (response.data.success) {
          // Clear OTP flow state
          sessionStorage.removeItem('admin_otp_flow');
          sessionStorage.removeItem('admin_otp_email');

          // verify-otp already stamped last_2fa_verification server-side -
          // nothing to do here client-side.

          // Store admin session
          localStorage.setItem('adminSession', JSON.stringify({
            email: storedEmail,
            loginTime: Date.now()
          }));
          
          setLoginSuccess(true);
          
          setTimeout(() => {
            navigate('/');
          }, 1500);
        } else {
          setError(response.data.message || 'Ugyldig verifiseringskode');
          setLoading(false);
        }
      } catch (error) {
        console.error('OTP verification error:', error);
        setError(error.response?.data?.message || 'Ugyldig kode. Prøv igjen.');
        setLoading(false);
      }
    }
  };

  if (loginSuccess) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card success">
          <div className="success-animation">
            <div className="checkmark">✓</div>
          </div>
          <h2>Innlogging vellykket!</h2>
          <p>Velkommen tilbake, Admin</p>
          <p>Sender deg til dashbordet...</p>
        </div>
      </div>
    );
  }

  if (showSelfRegister) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <img src={logo} alt="PlayWorld" className="admin-login-logo" />
            <h1>PlayWorld Admin</h1>
            <p>Er du en del av Playworld?</p>
          </div>

          {registerSent ? (
            <>
              <p style={{ textAlign: 'center', color: '#334155', fontSize: '14px', lineHeight: 1.5 }}>
                Sjekk innboksen til <strong>{registerEmail}</strong> - vi har sendt deg en lenke for å sette passord.
              </p>
              <button type="button" className="login-button" onClick={() => setShowSelfRegister(false)}>
                Tilbake til innlogging
              </button>
            </>
          ) : (
            <form onSubmit={handleSelfRegister} className="admin-login-form">
              <div className="form-group">
                <label htmlFor="registerName">Navn</label>
                <input
                  type="text"
                  id="registerName"
                  value={registerName}
                  onChange={(e) => setRegisterName(e.target.value)}
                  placeholder="Fornavn Etternavn"
                  disabled={registerLoading}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="registerEmail">Jobb-e-post</label>
                <input
                  type="email"
                  id="registerEmail"
                  value={registerEmail}
                  onChange={(e) => setRegisterEmail(e.target.value)}
                  placeholder="navn@playworld.no"
                  disabled={registerLoading}
                  autoComplete="email"
                  required
                />
              </div>

              {registerError && (
                <div className="error-message">
                  <span className="error-icon">⚠️</span>
                  {registerError}
                </div>
              )}

              <button type="submit" className="login-button" disabled={registerLoading}>
                {registerLoading ? 'Sender...' : 'Verifiser meg selv'}
              </button>

              <button
                type="button"
                onClick={() => { setShowSelfRegister(false); setRegisterError(''); }}
                style={{
                  background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer',
                  fontSize: '13px', marginTop: '12px', width: '100%', textAlign: 'center',
                  fontFamily: 'Poppins, sans-serif', textDecoration: 'underline',
                }}
              >
                Tilbake til innlogging
              </button>
            </form>
          )}

          <div className="admin-login-footer">
            <p>Kun for autorisert personell</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <img src={logo} alt="PlayWorld" className="admin-login-logo" />
          <h1>PlayWorld Admin</h1>
          <p>Administratorpålogging</p>
        </div>

        <form onSubmit={handleLogin} className="admin-login-form">
          <div className="form-group">
            <label htmlFor="email">Admin-e-post</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Skriv inn admin-e-posten din"
              disabled={loading || otpSent}
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Passord</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Skriv inn passordet ditt"
              disabled={loading || otpSent}
              autoComplete="current-password"
              required
            />
          </div>

          {otpSent && (
            <>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#e7f3ff', 
                color: '#004085', 
                borderRadius: '8px', 
                marginBottom: '16px',
                border: '1px solid #b8daff',
                textAlign: 'center',
                fontSize: '14px'
              }}>
                En 6-sifret verifiseringskode er sendt til <strong>{email}</strong>
              </div>

              <div className="form-group">
                <label htmlFor="otp">Verifiseringskode</label>
                <input
                  type="text"
                  id="otp"
                  inputMode="numeric"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  required
                  autoFocus
                  disabled={loading}
                  style={{ 
                    fontSize: '24px', 
                    letterSpacing: '8px', 
                    textAlign: 'center',
                    fontFamily: 'monospace'
                  }}
                />
              </div>

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={resendTimer > 0 || loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: resendTimer > 0 ? '#999' : '#010f3c',
                  cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  marginBottom: '12px',
                  textDecoration: 'underline',
                  width: '100%',
                  textAlign: 'center'
                }}
              >
                {resendTimer > 0 ? `Send ny kode om ${resendTimer}s` : 'Send ny kode'}
              </button>
            </>
          )}

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="login-button"
            disabled={loading || (otpSent && otp.length !== 6)}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                {otpSent ? 'Verifiserer...' : 'Logger inn...'}
              </>
            ) : (
              otpSent ? 'Bekreft kode' : 'Logg inn'
            )}
          </button>

          {otpSent && (
            <button
              type="button"
              onClick={async () => {
                // Clear OTP flow state and sign out - the session was left signed in
                // through the OTP wait, so cancelling here must not leave it dangling.
                sessionStorage.removeItem('admin_otp_flow');
                sessionStorage.removeItem('admin_otp_email');
                await supabase.auth.signOut();
                setOtpSent(false);
                setOtp('');
                setIsProcessing2FA(false);
                setError('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: '13px',
                marginTop: '12px',
                width: '100%',
                textAlign: 'center',
                fontFamily: 'Poppins, sans-serif',
                textDecoration: 'underline'
              }}
            >
              Avbryt og start på nytt
            </button>
          )}

          {!otpSent && (
            <button
              type="button"
              onClick={() => setShowSelfRegister(true)}
              style={{
                background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
                fontSize: '14px', marginTop: '12px', width: '100%', textAlign: 'center',
                fontFamily: 'Poppins, sans-serif', textDecoration: 'underline', fontWeight: 500,
              }}
            >
              Er du en del av Playworld? Verifiser deg selv →
            </button>
          )}
        </form>

        <div className="admin-login-footer">
          <p>Kun for autorisert personell</p>
          <small>For support, kontakt systemadministrator</small>
          <small className="admin-login-version">v{packageJson.version}</small>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
